import { request } from "./api";
import type {
  AvailabilityOverview,
  ScopedAvailability,
  ServiceDurationRule,
  TravelBufferRule,
} from "./types";

type ApiResponse<T> = { data: T };

export type ScopedAvailabilityInput = {
  employeeId?: string | null;
  teamId?: string | null;
  startAt: string;
  endAt: string;
  reason: string;
  notes?: string | null;
  active?: boolean;
};

export type DurationRuleInput = {
  serviceTypeId: string;
  name: string;
  active?: boolean;
  priority?: number;
  minimumBedrooms?: number | null;
  maximumBedrooms?: number | null;
  minimumBathrooms?: number | null;
  maximumBathrooms?: number | null;
  minimumSquareFeet?: number | null;
  maximumSquareFeet?: number | null;
  crewSize?: number | null;
  baseDurationMinutes: number;
  additionalMinutesPerBedroom?: number;
  additionalMinutesPerBathroom?: number;
  squareFeetBlockSize?: number | null;
  additionalMinutesPerSquareFeetBlock?: number;
  effectiveFrom: string;
  effectiveUntil?: string | null;
  notes?: string | null;
};

export type TravelRuleInput = {
  name: string;
  fromRegionId?: string | null;
  toRegionId?: string | null;
  bufferMinutes: number;
  priority?: number;
  effectiveFrom: string;
  effectiveUntil?: string | null;
  active?: boolean;
  notes?: string | null;
};

export async function getAvailability() {
  const response = await request<ApiResponse<AvailabilityOverview>>("/v1/admin/availability");
  return response.data;
}

export function createTimeOff(input: ScopedAvailabilityInput) {
  return mutate<ScopedAvailability>("/v1/admin/time-off", "timeOff", "POST", input);
}
export function updateTimeOff(id: string, input: Partial<ScopedAvailabilityInput>) {
  return mutate<ScopedAvailability>(`/v1/admin/time-off/${id}`, "timeOff", "PATCH", input);
}
export function createScheduleBlock(input: ScopedAvailabilityInput) {
  return mutate<ScopedAvailability>("/v1/admin/schedule-blocks", "scheduleBlock", "POST", input);
}
export function updateScheduleBlock(id: string, input: Partial<ScopedAvailabilityInput>) {
  return mutate<ScopedAvailability>(
    `/v1/admin/schedule-blocks/${id}`,
    "scheduleBlock",
    "PATCH",
    input,
  );
}
export function createDurationRule(input: DurationRuleInput) {
  return mutate<ServiceDurationRule>("/v1/admin/duration-rules", "durationRule", "POST", input);
}
export function updateDurationRule(id: string, input: Partial<DurationRuleInput>) {
  return mutate<ServiceDurationRule>(
    `/v1/admin/duration-rules/${id}`,
    "durationRule",
    "PATCH",
    input,
  );
}
export function createTravelRule(input: TravelRuleInput) {
  return mutate<TravelBufferRule>(
    "/v1/admin/travel-buffer-rules",
    "travelBufferRule",
    "POST",
    input,
  );
}
export function updateTravelRule(id: string, input: Partial<TravelRuleInput>) {
  return mutate<TravelBufferRule>(
    `/v1/admin/travel-buffer-rules/${id}`,
    "travelBufferRule",
    "PATCH",
    input,
  );
}

async function mutate<T>(path: string, key: string, method: "POST" | "PATCH", input: object) {
  const response = await request<ApiResponse<Record<string, T>>>(path, {
    method,
    body: JSON.stringify(input),
  });
  return response.data[key];
}
